<template>
    <b-modal id="classify-dispatch-assets" size="xl" title="Classify Assets" ok-title="Save" ref="modal" @ok="handleOk"
        @hidden="onReset" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
        :no-close-on-backdrop="true">
        <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

        <b-form @submit.stop.prevent="handleSubmit" novalidate>
            <b-container fluid>
                <div>
                    <b-tabs>
                        <b-tab title="Primary Details" active>
                            <b-row class="mb-2">
                                <b-col lg="2">
                                    <label class="font-weight-bold pt-0 mb-0">Dispatch No.:</label>
                                </b-col>
                                <b-col>
                                    <label class="numFont mb-0">{{ dispatchNo }}</label>
                                </b-col>
                            </b-row>
                            <b-row class="mb-2">
                                <b-col lg="2">
                                    <label class="font-weight-bold pt-0 mb-0">Area:</label>
                                </b-col>
                                <b-col>
                                    <label class="numFont mb-0">{{ areaDestination }}</label>
                                </b-col>
                            </b-row>

                            <hr />

                            <div class="asset-type-wrapper">
                                <template v-for="(asset, index) in form.assetTypes">
                                    <b-row class="mb-4 align-items-center" :key="index + 'row1'">
                                        <b-col lg="2">
                                            <label class="font-weight-bold pt-0 mb-0">Asset Type:</label>
                                        </b-col>
                                        <b-col>
                                            <label class="mb-0">{{ asset.assetType }}</label>
                                        </b-col>
                                    </b-row>

                                    <b-row :key="index + 'row2'">
                                        <b-col lg="6" sm="12">
                                            <b-form-group label="Select Condition" label-for="Select Condition"
                                                label-class="font-weight-bold pt-0"
                                                description="Select additional condition(s) under this asset type">
                                                <div class="d-flex align-items-center gap-2">
                                                    <v-select id="select-condition" name="Select Condition"
                                                        class="style-chooser flex-grow-1" label="text"
                                                        :value="form.conditionSelected[index]"
                                                        @input="onConditionChange(index, $event)"
                                                        :options="form.conditionOptions[index]"
                                                        :reduce="(condition) => condition.value">
                                                        <template v-slot:no-options="{ search, searching }">
                                                            <template v-if="searching">
                                                                No results found for <em><strong>{{ search
                                                                        }}</strong></em>
                                                            </template>
                                                            <em :style="{ opacity: 0.5 }" v-else>
                                                                Start typing to search for an asset type
                                                            </em>
                                                        </template>
                                                    </v-select>

                                                    <b-button class="add-condition" variant="primary"
                                                        @click="onAddCondition(index)">
                                                        Add
                                                    </b-button>
                                                </div>
                                            </b-form-group>

                                            <b-form-group>
                                                <div class="d-flex align-items-center gap-2">
                                                    <b-col lg="3" class="text-center">
                                                        <label class="font-weight-bold mb-0">Condition</label>
                                                    </b-col>
                                                    <b-col lg="6" class="text-center">
                                                        <label class="font-weight-bold mb-0">Quantity</label>
                                                    </b-col>
                                                    <b-col lg="3" class="text-center">
                                                        <label class="font-weight-bold mb-0">Actions</label>
                                                    </b-col>
                                                </div>
                                            </b-form-group>
                                            <b-form-group v-for="(condition, key) in form.conditionList[index]"
                                                :key="key">
                                                <div class="d-flex align-items-center gap-2">
                                                    <b-col lg="3" class="text-center">
                                                        <span>{{ condition.condition }}</span>
                                                    </b-col>
                                                    <b-col lg="6" class="text-center">
                                                        <div class="input-group">
                                                            <div class="input-group-prepend">
                                                                <button
                                                                    class="btn btn-outline-secondary decrement-button"
                                                                    type="button" @click="onIncrement(index, key, -1)">
                                                                    -
                                                                </button>
                                                            </div>
                                                            <b-form-input id="quantity" name="Quantity" type="number"
                                                                :key="`${index}-${key}`"
                                                                v-model.number="form.conditionList[index][key].value"
                                                                v-validate="'required|min_value:0'" :min="1"
                                                                class="text-center numFont"
                                                                @input="onQuantityInput($event, index, key)"
                                                                @keydown="preventInvalidKey" />
                                                            <div class="input-group-append">
                                                                <button
                                                                    class="btn btn-outline-secondary increment-button"
                                                                    type="button" @click="onIncrement(index, key, 1)">
                                                                    +
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </b-col>
                                                    <b-col lg="3" class="text-center">
                                                        <b-form-group class="mb-0 text-center">
                                                            <b-button :disabled="isConditionDefault(index, key)"
                                                                size="sm" v-b-tooltip.hover.top="'Delete Billable Rate'"
                                                                :variant="isConditionDefault(index, key)
                                                                    ? 'secondary'
                                                                    : 'danger'"
                                                                @click.stop="deleteCondition(index, key)"
                                                                class="mr-1 mt-1">
                                                                <em class="fa fa-trash"></em>
                                                            </b-button>
                                                        </b-form-group>
                                                    </b-col>
                                                </div>
                                            </b-form-group>
                                        </b-col>

                                        <b-col lg="6">
                                            <AssetConditionsChart :data="conditionData(index).values"
                                                :labels="conditionData(index).conditions" />
                                        </b-col>
                                    </b-row>

                                    <hr :key="index + 'hr'" />
                                </template>
                            </div>
                        </b-tab>

                        <b-tab title="Documents">
                            <b-row>
                                <b-col lg="6" md="6" sm="12">
                                    <b-form-group label="Supporting Documents" label-for="Supporting Documents"
                                        label-class="font-weight-bold pt-0" />

                                    <div v-show="noOfDocuments >= 1">
                                        <b-row class="mt-4" no-gutters>
                                            <b-col sm="11">
                                                <b-form-group label="Document 1" label-for="Document 1"
                                                    description="*32-bit PNG, 1000px by 1000px, up to 1 MB">
                                                    <b-form-file id="Document 1" v-model="files[0]"
                                                        placeholder="Choose image" ref="document-1"
                                                        accept="image/png, image/jpeg, image/jpg"
                                                        @change="onSelectDocument($event, 0)">

                                                        <template slot="file-name" slot-scope="{ names }">
                                                            <b-badge variant="dark">{{ names[0] }}</b-badge>
                                                        </template>
                                                    </b-form-file>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>

                                        <!-- Preview -->
                                        <div class="document-file" v-if="isNonNullDocument(0)">
                                            <i @click="onShowDocument(selDocuments[0])">
                                                {{ selDocuments[0].name }}
                                            </i>
                                            <em @click="onResetDocument(0)" class="fa fa-times-circle"></em>
                                        </div>
                                    </div>

                                    <div v-show="noOfDocuments >= 2">
                                        <b-row class="mt-4" no-gutters>
                                            <b-col sm="11">
                                                <b-form-group label="Document 2" label-for="Document 2"
                                                    description="*32-bit PNG, 1000px by 1000px, up to 1 MB">
                                                    <b-form-file id="Document 2" v-model="files[1]"
                                                        placeholder="Choose image" ref="document-2"
                                                        accept="image/png, image/jpeg, image/jpg"
                                                        @change="onSelectDocument($event, 1)">

                                                        <template slot="file-name" slot-scope="{ names }">
                                                            <b-badge variant="dark">{{ names[0] }}</b-badge>
                                                        </template>
                                                    </b-form-file>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>

                                        <!-- Preview -->
                                        <div class="document-file" v-if="isNonNullDocument(1)">
                                            <i @click="onShowDocument(selDocuments[1])">
                                                {{ selDocuments[1].name }}
                                            </i>
                                            <em @click="onResetDocument(1)" class="fa fa-times-circle"></em>
                                        </div>
                                    </div>

                                    <div v-show="noOfDocuments >= 3">
                                        <b-row class="mt-4" no-gutters>
                                            <b-col sm="11">
                                                <b-form-group label="Document 3" label-for="Document 3"
                                                    description="*32-bit PNG, 1000px by 1000px, up to 1 MB">
                                                    <b-form-file id="Document 3" v-model="files[2]"
                                                        placeholder="Choose image" ref="document-3"
                                                        accept="image/png, image/jpeg, image/jpg"
                                                        @change="onSelectDocument($event, 2)">

                                                        <template slot="file-name" slot-scope="{ names }">
                                                            <b-badge variant="dark">{{ names[0] }}</b-badge>
                                                        </template>
                                                    </b-form-file>
                                                </b-form-group>
                                            </b-col>
                                        </b-row>

                                        <!-- Preview -->
                                        <div class="document-file" v-if="isNonNullDocument(2)">
                                            <i @click="onShowDocument(selDocuments[2])">
                                                {{ selDocuments[2].name }}
                                            </i>
                                            <em @click="onResetDocument(2)" class="fa fa-times-circle"></em>
                                        </div>
                                    </div>

                                    <b-button class="add-document" variant="primary" @click="onAddDocument">
                                        Add Document
                                    </b-button>
                                </b-col>

                                <b-col lg="6" md="6" sm="12">
                                    <b-form-group class="mt-4" label="Remarks" label-for="Remarks" description
                                        label-class="font-weight-bold pt-0">
                                        <b-form-textarea name="Remarks" type="text" v-model="form.remarks"
                                            maxlength="200" v-validate="getValidationParam(true, remarksRegex)"
                                            :rows="3" placeholder="Remarks" />
                                        <span v-show="errors.has('Remarks')" class="help-block">
                                            {{ errors.first('Remarks') }}
                                        </span>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </b-tab>
                    </b-tabs>
                </div>
            </b-container>
        </b-form>
    </b-modal>
</template>

<script>
// Utils
import { DateUtil } from '@/utils/dateutil';
import { DispatchUtil } from '@/utils/dispatchUtil';
import { FileUtil } from '@/utils/fileUtil';
import { ValidationUtil } from '@/utils/validationUtil';

// API & DAO
import dispatchApi from '@/api/dispatchApi';
import classificationDAO from '@/database/classifications';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import { storage } from '@/config/firebase';
import _ from 'lodash';

export default {
    name: 'classify-dispatch-assets',
    components: {
        Loading,
        AssetConditionsChart: () => import("@/views/commons/charts/AssetConditionsChart"),
    },
    data() {
        return {
            form: {},

            selDispatch: {},

            selDocuments: [null, null, null],
            files: [null, null, null],
            noOfDocuments: 2,

            conditionOptions: [],

            allClassifications: {},
            allAssetTypesObj: { ...this.$store.getters.assetTypes },

            loggedUser: this.$store.getters.loggedUser,

            // Check for loader
            isLoading: false,
        }
    },
    computed: {
        disableConfirmButtons() {
            return this.isLoading;
        },
        remarksRegex() {
            return config.remarksRegex;
        },
        dispatchId() {
            let dispatch = this.selDispatch ? this.selDispatch : {};
            return dispatch.dispatchId ? dispatch.dispatchId : '';
        },
        dispatchNo() {
            let dispatch = this.selDispatch ? this.selDispatch : {};
            return dispatch.dispatchNo ? dispatch.dispatchNo : '';
        },
        areaDestination() {
            let destination = this.selDispatch && this.selDispatch.destination ? this.selDispatch.destination : null;
            return destination ? DispatchUtil.getCompanyLocationDisplay(destination) : '';
        }
    },
    mounted() {
        EventBus.$on('onClassifyDispatchAssets', (params) => {
            this.updateData(params);
        });
    },
    methods: {
        preventInvalidKey(event) {
            const invalidChars = ['e', 'E', '+', '-', '.'];
            if (invalidChars.includes(event.key)) {
                event.preventDefault();
            }
        },
        onQuantityInput(currentValue, index, key) {
            this.$nextTick(() => {
                const maxValueAllowed = this.form.dispatch.assets[index].actualQuantity;

                const currentTotalValueWithoutCurrentKey = this.form.conditionList[index]
                    .map((item, i) => i === key ? 0 : parseInt(item.value) || 0)
                    .reduce((acc, val) => acc + val, 0);

                const parsedValue = parseInt(currentValue) || 0;

                if ((currentTotalValueWithoutCurrentKey + parsedValue) > maxValueAllowed) {
                    this.$set(this.form.conditionList[index][key], 'value', maxValueAllowed - currentTotalValueWithoutCurrentKey);
                }
            });

            this.$forceUpdate();
        },
        onConditionChange(index, value) {
            this.$set(this.form.conditionSelected, index, value);
            this.$forceUpdate();
        },
        conditionData(index) {
            return {
                values: _.map(_.values(this.form.conditionList[index]), 'value'),
                conditions: _.map(_.values(this.form.conditionList[index]), 'condition'),
            };
        },
        async retrieveClassifications(dispatch) {
            this.allClassifications = {};

            const classificationsObj = await classificationDAO.getClassificationsByDispatchId(dispatch.id);
            _.forEach(classificationsObj, (classification) => {
                this.allClassifications[classification.assetType] = classification;
            });
        },
        getValidationParam(isRequired, regex) {
            return {
                required: isRequired,
                regex: regex,
            };
        },

        // Conditions
        onAddCondition(index) {
            const selectedCondition = this.form.conditionSelected[index];

            if (!selectedCondition) {
                this.$toaster.warning('Please select condition to proceed.');
            } else if (selectedCondition && _.find(this.form.conditionList[index], x => x.id === selectedCondition.id)) {
                this.$toaster.warning('Condition already included on the list')
            } else {
                this.form.conditionList[index].push({
                    id: selectedCondition.id,
                    condition: selectedCondition.condition,
                    value: 0,
                    description: selectedCondition.description,
                })

                this.$forceUpdate();
            }
        },
        deleteCondition(index, key) {
            this.$delete(this.form.conditionList[index], key);

            this.$forceUpdate();
        },
        isConditionDefault(index, key) {
            return this.form.conditionList[index][key].description === 'Default';
        },
        onIncrement(index, key, value) {
            const maxValueAllowed = this.form.dispatch.assets[index].actualQuantity;

            const currentTotalValue = _.sum(
                this.form.conditionList[index].map(item => parseInt(item.value) || 0)
            );

            const currentRow = [...this.form.conditionList[index]];
            const field = { ...currentRow[key] };

            const currentValue = parseInt(field.value || 0);
            const newValue = currentValue + value;

            if ((currentTotalValue + value > maxValueAllowed) || newValue < 0) {
                return;
            }

            field.value = newValue;
            currentRow[key] = field;

            this.$set(this.form.conditionList, index, currentRow);

            // This should trigger reactivity
            this.$forceUpdate();
        },

        async handleOk(evt) {
            // Prevent modal from closing
            evt.preventDefault();

            // show loading indicator
            this.isLoading = true;

            let isValid = await this.$validator.validateAll();
            if (!isValid) {
                this.$toaster.warning('Please address the field/s with invalid input.');
                this.isLoading = false;
                return;
            }

            let isValidAccount = this.validateClassification();
            if (!isValidAccount) {
                this.isLoading = false;
                return;
            }

            await this.handleSubmit();
        },
        validateClassification() {
            let isValid = true;

            if (!this.hasUploadedDocs()) {
                this.$toaster.warning('Please upload at least 1 supporting document to proceed.');
                isValid = false;
            } else if (this.hasInvalidDocs()) {
                this.$toaster.warning("Please check if all documents uploaded have a file type of PNG or JPG only.");
                isValid = false;
            } else if (!_.isEmpty(this.form.conditionList)) {
                let hasMissingClassification = false;

                for (const [index, condition] of this.form.conditionList.entries()) {
                    const maxValueAllowed = this.form.dispatch.assets[index].actualQuantity;
                    const totalClassified = _.sum(condition.map(item => parseInt(item.value) || 0));

                    if (totalClassified < maxValueAllowed) {
                        hasMissingClassification = true;
                        break;
                    }
                }

                if (hasMissingClassification) {
                    this.$toaster.warning(`Please classify the remaining assets to their respective conditions.`);
                    isValid = false;
                }
            }

            return isValid;
        },
        hasUploadedDocs() {
            let uploadedDocs = _.filter(this.selDocuments, document => {
                return document && !_.isEmpty(document);
            });
            return _.size(uploadedDocs) >= 1;
        },
        hasInvalidDocs() {
            let invalidDocs = _.filter(this.selDocuments, document => {
                return document !== null && document instanceof File;
            });
            return !_.isEmpty(invalidDocs);
        },

        getClassificationObj() {
            const classificationList = [];

            _.forEach(this.form.conditionList, (condition, index) => {
                const asset = this.form.dispatch.assets[index];

                const classification = {
                    collectionName: 'dispatches',
                    collectionId: this.selDispatch.id,
                    assetType: asset.assetType,
                    assetTypeId: asset.assetTypeId,
                    conditions: _.map(Object.values(condition), x => ({
                        id: x.id,
                        condition: x.condition,
                        description: x.description,
                        quantity: x.value,
                    })),
                    documents: [],
                    dispatchId: this.selDispatch.id,
                    dispatchNo: this.selDispatch.dispatchNo,
                    remarks: this.form.remarks,
                    isCompleted: true,
                    area: this.selDispatch.source,
                    dateCreated: DateUtil.getCurrentTimestamp(),
                    dateUpdated: DateUtil.getCurrentTimestamp(),
                    dateClassified: DateUtil.getCurrentTimestamp(),
                    createdBy: this.loggedUser.id,
                    updatedBy: this.loggedUser.id,
                    classifiedBy: this.loggedUser.id,
                }

                for (const document of this.selDocuments) {
                    if (document && !_.isEmpty(document)) {
                        let documentObj = document;
                        if (documentObj.isNew) {
                            documentObj = {
                                name: document.name,
                                url: document.url,
                                geoaddress: { latitude: 0.0, longitude: 0.0 },
                            };
                        }
                        classification.documents.push(documentObj);
                    } else {
                        classification.documents.push(null);
                    }
                }

                classificationList.push(classification);
            });

            return classificationList;
        },
        async handleSubmit() {
            // show loading indicator
            this.isLoading = true;

            try {
                let classificationList = this.getClassificationObj();
                let { data } = await dispatchApi.saveMultipleClassification(
                    classificationList,
                    this.loggedUser.id,
                    DateUtil.getCurrentTimestamp()
                );

                if (data.isSuccess) {
                    // upload document forms
                    await this.uploadDocuments(data.classificationArr);

                    this.$toaster.success(data.message);
                    this.$refs.modal.hide();

                } else {
                    this.$toaster.warning(data.message);
                }
            } catch (error) {
                console.error('Error saving classification(s):', error);
                this.$toaster.error(`Error saving classification(s). Please try again.`);
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        },

        // Documents
        async uploadDocuments(classificationArr) {
            for (let i = 0; i < this.selDocuments.length; i++) {
                const document = this.selDocuments[i];
                if (document && document.isNew && document.file instanceof File) {

                    const result = await this.firebaseUpload(document);
                    if (result.name && result.url) {
                        _.forEach(classificationArr, classification => {
                            classification.documents[i].url = result.url;
                        });
                    }
                }
            }

            await dispatchApi.saveMultipleClassification(classificationArr, this.loggedUser.id, DateUtil.getCurrentTimestamp());
        },
        async firebaseUpload(document) {
            return new Promise((resolve, reject) => {
                let storageRef = storage.ref(`${document.fbStoragePath}/${document.name}`);
                let task = storageRef.put(document.file);

                task.on(
                    'state_changed',
                    () => {
                        // let percentage = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    },
                    (error) => {
                        reject(error);
                    },
                    () => {
                        task.snapshot.ref.getDownloadURL().then((downloadURL) => {
                            resolve({
                                name: document.name,
                                url: downloadURL,
                            });
                        });
                    }
                );
            });
        },
        onShowDocument(doc) {
            let url = doc.url;
            let fileName = doc.name;
            EventBus.$emit("onSelectImageView", { url: url, name: fileName });
            this.$bvModal.show("image-view-dialog");
        },
        onSelectDocument(evt, id) {
            const file = evt.target.files[0];

            if (!FileUtil.isValidImgFileType(file)) {
                this.$toaster.error("Invalid File Type: Please import a file in PNG or JPEG format.");
                return;
            }

            const vm = this;
            const url = URL.createObjectURL(file);
            let dimensions = { w: 0, h: 0 };

            const image = new Image();
            image.onload = function () {
                dimensions.w = image.width;
                dimensions.h = image.height;

                if (dimensions.w > 1000 || dimensions.h > 1000) {
                    vm.$toaster.warning("The supporting document width and height shouldn't be greater than 1000 pixels.");
                } else {
                    vm.$set(vm.selDocuments, id, {
                        name: `${vm.selDispatch.dispatchId}_doc_${id}.jpg`,
                        url: url,
                        file: file,
                        fbStoragePath: 'images/classifications',
                        isNew: true,
                    });
                }
            };
            image.src = url;
        },
        onAddDocument() {
            if (this.noOfDocuments < 3) {
                this.noOfDocuments++;
            } else if (this.noOfDocuments === 3) {
                this.$toaster.warning('You can only add up to 3 documents only');
            }
        },
        onResetDocument(id) {
            this.$set(this.selDocuments, id, null);
            this.$set(this.files, id, null);
        },
        isNonNullDocument(id) {
            return this.selDocuments[id] && !_.isEmpty(this.selDocuments[id]);
        },
        onReset() {
            this.form = {};
        },
        async updateData(params) {
            this.isLoading = true;

            await this.retrieveClassifications(params.dispatch);

            if (this.allClassifications && !_.isEmpty(this.allClassifications)) {
                this.form = { ...this.allClassifications[params.dispatch.name] };
            } else {
                this.form = { ...config.classificationDefault };

                this.form.collectionName = 'dispatches';
                this.form.collectionId = params.dispatch.id;
                this.form.dispatchId = params.dispatch.dispatchId;
                this.form.dispatchNo = params.dispatch.dispatchNo;

                // timestamps
                this.form.dateCreated = DateUtil.getCurrentTimestamp();
                this.form.createdBy = this.loggedUser.id;
            }

            this.form.conditionSelected = [];
            this.form.conditionOptions = [];
            this.form.conditionList = [];
            this.form.dispatch = params.dispatch;
            this.form.assetTypes = params.assetTypes;
            this.selDispatch = params.dispatch;

            this.preloadActiveConditions();

            // init selected documents
            this.noOfDocuments = 0;
            if (_.isEmpty(this.form.documents)) {
                this.selDocuments = [null, null, null];
                this.files = [null, null, null];
                this.noOfDocuments = 1;
            } else {
                this.selDocuments = [];
                for (const document of this.form.documents) {
                    if (document && ValidationUtil.isValidURL(document.url)) {
                        this.selDocuments.push(document);
                    } else {
                        this.selDocuments.push(null);
                    }
                    this.noOfDocuments++;
                }
            }

            this.isLoading = false;
        },
        preloadActiveConditions() {
            _.forEach(this.form.assetTypes, (assetTypes, index) => {
                const asset = this.form.dispatch.assets[index];
                const assetTypeObj = this.allAssetTypesObj[assetTypes.assetTypeId];
                const conditions = assetTypeObj.conditions;

                const conditionOptions = [{ value: null, text: ' - Please select - ' }];
                const formConditionList = [];

                for (const [index, condition] of conditions.entries()) {
                    if (condition.isActive) {
                        conditionOptions.push({ value: condition, text: condition.condition });

                        // Good and Damaged
                        if (index <= 1) {
                            formConditionList.push({
                                id: condition.id,
                                condition: condition.condition,
                                value: index === 0 ? asset.actualQuantity : 0,
                                description: condition.description,
                            })
                        }
                    }
                }

                this.form.conditionSelected.push({ value: null, text: ' - Please select - ' });
                this.form.conditionOptions.push(conditionOptions);
                this.form.conditionList.push(formConditionList);
            });
        },
    },
    beforeDestroy() {
        EventBus.$off('onClassifyDispatchAssets');
    }
}

</script>

<style scoped>
.add-condition {
    margin-left: 10px;
}

.decrement-button {
    width: 35px;
    border-top-left-radius: 5px !important;
    border-bottom-left-radius: 5px !important;
}

.increment-button {
    width: 35px;
    border-top-right-radius: 5px !important;
    border-bottom-right-radius: 5px !important;
}

.orange {
    background-color: #E48F01;
}

.blue {
    background-color: #122B91;
}

.item-count {
    font-size: 30px;
}

.item-label {
    font-size: 16px;
}

.vertical-center {
    margin-top: 15px;
    margin-left: 20px;
}

.label-header {
    font-weight: bold;
}

.document-file {
    font-weight: bold;
    margin-top: -1em;
    margin-bottom: 0.5em;
    cursor: pointer;
}

.document-file i {
    color: #E48F01;
    margin-right: 0.5em;
}

.asset-type-wrapper {
    overflow-y: auto;
    overflow-x: hidden;

    @media (min-width: 768px) {
        max-height: 20vh;
    }

    @media (min-width: 992px) {
        max-height: 35vh;
    }

    @media (min-width: 1200px) {
        max-height: 50vh;
    }

    @media (min-width: 1400px) {
        max-height: 55vh;
    }
}
</style>